import { FC, JSX, useCallback, useContext, useMemo, useRef } from "react";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ClaimsActivityLineOfBusinessTypeEnum, ClaimsActivityVolumeLabelEnum } from "@app/projects/network-assessment/pci-dashboard/claims-activity";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { ClaimsVolumeFilterFieldKey } from "@app/projects/network-assessment/provider-hub/interfaces";
import EmptyWidgetMessage from "@app/projects/network-assessment/provider-hub/pci-dashboard/EmptyWidgetMessage";
import { useClaimsVolumeBreakdown } from "@app/projects/network-assessment/provider-hub/pci-dashboard/hooks/useClaimsVolumeBreakdown";
import { useColumnHighlightRect } from "@app/projects/network-assessment/provider-hub/pci-dashboard/hooks/useColumnHighlightRect";
import { useEffectivenessEfficiencyBreakdown } from "@app/projects/network-assessment/provider-hub/pci-dashboard/hooks/useEffectivenessEfficiencyBreakdown";
import { useReadyForReview } from "@app/projects/network-assessment/provider-hub/pci-dashboard/hooks/useReadyForReview";
import ProviderScoreBarChart from "@app/projects/network-assessment/provider-hub/pci-dashboard/ProviderScoreBarChart";
import ReadyForReviewSpecialtyList from "@app/projects/network-assessment/provider-hub/pci-dashboard/ReadyForReviewSpecialtyList";
import { ProviderHubBasicFilters } from "@app/projects/network-assessment/provider-hub/ProvidersHubBasicFilters";
import InfoIndicator from "@app/shared/InfoIndicator";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { ColumnConfig } from "@app/shared/waffles";
import { TextAlignment, wafflesKeyColumnName } from "@app/shared/waffles/exports";
import { Waffles } from "@app/shared/waffles/Waffles";

interface ClaimsVolumeBreakdownWaffleToppings {
  [wafflesKeyColumnName]: string;
  providerStatus: JSX.Element;
  total: JSX.Element;
  commercial: JSX.Element;
  medicaid: JSX.Element;
  medicare: JSX.Element;
  va: JSX.Element;
  tricare: JSX.Element;
}

const CLAIMS_VOLUME_LABEL_ROW_ORDER = [
  ClaimsActivityVolumeLabelEnum.Core,
  ClaimsActivityVolumeLabelEnum.Standard,
  ClaimsActivityVolumeLabelEnum.Peripheral,
  ClaimsActivityVolumeLabelEnum.RxOnly,
  ClaimsActivityVolumeLabelEnum.InsufficientData,
  ClaimsActivityVolumeLabelEnum.Ghost,
];

const CLAIMS_VOLUME_LABEL_DESCRIPTIONS: Record<ClaimsActivityVolumeLabelEnum, string> = {
  [ClaimsActivityVolumeLabelEnum.Core]: "75th+ percentile in patient volume (based on population density and specialty)",
  [ClaimsActivityVolumeLabelEnum.Standard]: "25th-74th percentile in patient volume (based on population density and specialty)",
  [ClaimsActivityVolumeLabelEnum.Peripheral]: "<24th percentile in patient volume (based on population density and specialty)",
  [ClaimsActivityVolumeLabelEnum.RxOnly]: "Claims in which only Prescriptions are present",
  [ClaimsActivityVolumeLabelEnum.InsufficientData]: "Not enough data available to make a designation",
  [ClaimsActivityVolumeLabelEnum.Ghost]: "0 patients",
  [ClaimsActivityVolumeLabelEnum.Total]: "",
}

type LobColumnKey = "total" | "commercial" | "medicaid" | "medicare" | "va" | "tricare";

const LINE_OF_BUSINESS_COLUMNS: { key: LobColumnKey; lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum; filterFieldKey: ClaimsVolumeFilterFieldKey }[] = [
  /* oxlint-disable sort-keys */
  { key: "total", lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum.Total, filterFieldKey: "totalClaimsVolume" },
  { key: "medicare", lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum.Medicare, filterFieldKey: "medicareClaimsVolume" },
  { key: "medicaid", lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum.Medicaid, filterFieldKey: "medicaidClaimsVolume" },
  { key: "commercial", lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum.Commercial, filterFieldKey: "commercialClaimsVolume" },
  { key: "va", lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum.VA, filterFieldKey: "vaClaimsVolume" },
  { key: "tricare", lineOfBusiness: ClaimsActivityLineOfBusinessTypeEnum.TRICARE, filterFieldKey: "tricareClaimsVolume" },
  /* oxlint-enable sort-keys */
];

const getInsufficientDataTooltipContent = (): JSX.Element => (
  <div className="text-start">
    No claims activity, but not labeled a ghost for one of these reasons:<br />
    <b>New NPI</b>: Recently issued, no claims history yet<br />
    <b>Limited state/LOB data</b>: Limited claims coverage in this state or line of business<br />
    <b>Limited specialty data</b>: Limited claims volume for this specialty<br />
    <b>Indirect care only</b>: Claims exist for referring, supervising, or ordering, but not direct care<br />
  </div>
);

const renderClaimsVolumeLabelCell = (claimsVolumeLabel: ClaimsActivityVolumeLabelEnum) => (
  <>
    <div>{claimsVolumeLabel}</div>
    <div className="pci-dashboard__description-text">
      {CLAIMS_VOLUME_LABEL_DESCRIPTIONS[claimsVolumeLabel]} {claimsVolumeLabel === ClaimsActivityVolumeLabelEnum.InsufficientData && (
        <InfoIndicator className={"informational-indicator pb2"} tooltipTitle={getInsufficientDataTooltipContent()} />
      )}
    </div>
  </>
);

const ProviderHubProviderClaimsInsightsView: FC = () => {
  const providerHubContext = useContext(ProviderHubContext);
  const projectContext = useContext(CountyProjectContext);

  /** Prevent a stale closure around viewProvidersFilteredByClaimsVolume, mostly so filtersWereDirtyBeforeClick
   * properly stays in sync. See User Story 209926 for more details.
   */
  const viewProvidersFilteredByClaimsVolumeRef = useRef(providerHubContext.viewProvidersFilteredByClaimsVolume);
  viewProvidersFilteredByClaimsVolumeRef.current = providerHubContext.viewProvidersFilteredByClaimsVolume;

  const providerStatusBreakdownTableWrapperRef = useRef<HTMLDivElement>(null);

  const readyForReview = useReadyForReview(providerHubContext.appliedProviderFilters);
  const claimsVolumeBreakdown = useClaimsVolumeBreakdown(providerHubContext.appliedProviderFilters);
  const effectivenessEfficiencyBreakdown = useEffectivenessEfficiencyBreakdown(providerHubContext.appliedProviderFilters);

  const providerStatusBreakdownColumnConfig: ColumnConfig = {
    /* oxlint-disable sort-keys */
    providerStatus: {
      displayText: "Provider Status",
      sizeContentToFit: true,
      minWidth: "28.4rem",
      testId: "pci-dashboard__claims-volume-breakdown-table_provider-status",
      noSort: true,
      noResize: true,
    },
    total: {
      displayText: "Total",
      sizeContentToFit: true,
      minWidth: "6.7rem",
      testId: "pci-dashboard__claims-volume-breakdown_total",
      noSort: true,
      alignment: TextAlignment.Right,
      noResize: true,
    },
    medicare: {
      displayText: "Medicare",
      sizeContentToFit: true,
      minWidth: "6.7rem",
      testId: "pci-dashboard__claims-volume-breakdown_medicare",
      noSort: true,
      alignment: TextAlignment.Right,
      noResize: true,
    },
    medicaid: {
      displayText: "Medicaid",
      sizeContentToFit: true,
      minWidth: "6.7rem",
      testId: "pci-dashboard__claims-volume-breakdown_medicaid",
      noSort: true,
      alignment: TextAlignment.Right,
      noResize: true,
    },
    commercial: {
      displayText: "Commercial",
      sizeContentToFit: true,
      minWidth: "6.7rem",
      testId: "pci-dashboard__claims-volume-breakdown_commercial",
      noSort: true,
      alignment: TextAlignment.Right,
      noResize: true,
    },
    va: {
      displayText: "VA",
      sizeContentToFit: true,
      minWidth: "6.7rem",
      testId: "pci-dashboard__claims-volume-breakdown_va",
      noSort: true,
      alignment: TextAlignment.Right,
      noResize: true,
      hidden: !providerHubContext.showVaFilters,
    },
    tricare: {
      displayText: "TRICARE",
      sizeContentToFit: true,
      minWidth: "6.7rem",
      testId: "pci-dashboard__claims-volume-breakdown_tricare",
      noSort: true,
      alignment: TextAlignment.Right,
      noResize: true,
      hidden: !providerHubContext.showTriCareFilters,
    },
    /* oxlint-enable sort-keys */
  };

  const providerStatusBreakdownVisibleColumns = Object.entries(providerStatusBreakdownColumnConfig).filter(([, column]) => !column.hidden);

  const handleClaimsVolumeCellClick = useCallback((filterFieldKey: ClaimsVolumeFilterFieldKey, claimsVolumeLabel: ClaimsActivityVolumeLabelEnum) => {
    viewProvidersFilteredByClaimsVolumeRef.current(filterFieldKey, claimsVolumeLabel);
  }, []);

  const renderProviderCountCell = (providerCount: number, percent: number, lobFilterFieldKey: ClaimsVolumeFilterFieldKey, claimsVolumeLabel: ClaimsActivityVolumeLabelEnum): JSX.Element => {
    return (
      <>
        {providerCount ? (
          <QESButton
            qesType="link"
            qesTestId={`pci-dashboard__claims-volume-breakdown--${lobFilterFieldKey}-${claimsVolumeLabel}`}
            onClick={() => handleClaimsVolumeCellClick(lobFilterFieldKey, claimsVolumeLabel)}
          >
            <b className="pci-dashboard__provider-count">{providerCount.toLocaleString()}</b>
          </QESButton>
        ) : (
          <b className="pci-dashboard__provider-count">0</b>
        )}
        <div className="pci-dashboard__description-text">{percent.toFixed(1)}%</div>
      </>
    );
  };

  const totalProvidersPerLOB: Record<ClaimsActivityLineOfBusinessTypeEnum, number> = useMemo(() => {
    const totals = Object.fromEntries(
      LINE_OF_BUSINESS_COLUMNS.map(({ lineOfBusiness }) => [lineOfBusiness, 0])
    ) as Record<ClaimsActivityLineOfBusinessTypeEnum, number>;

    claimsVolumeBreakdown.data?.forEach((s) => {
      totals[s.lineOfBusiness] += s.providerCount;
    });

    return totals;
  }, [claimsVolumeBreakdown.data]);

  const claimsVolumeBreakdownTableData: ClaimsVolumeBreakdownWaffleToppings[] = useMemo(() => {
    const rows = CLAIMS_VOLUME_LABEL_ROW_ORDER.map((label) => {
      const row = { key: label, providerStatus: renderClaimsVolumeLabelCell(label) } as ClaimsVolumeBreakdownWaffleToppings;
      LINE_OF_BUSINESS_COLUMNS.forEach(({ filterFieldKey, key, lineOfBusiness }) => {
        const providerCount = claimsVolumeBreakdown.data?.find(s => s.claimsVolumeLabel === label && s.lineOfBusiness === lineOfBusiness)?.providerCount ?? 0;
        const lobTotalProviderCount = totalProvidersPerLOB[lineOfBusiness];
        const percent = lobTotalProviderCount ? 100 * providerCount / lobTotalProviderCount : 0;
        row[key] = renderProviderCountCell(providerCount, percent, filterFieldKey, label);
      });
      return row;
    });

    rows.push({
      /* oxlint-disable sort-keys */
      key: "totals-footer",
      providerStatus: <b>Total Providers</b>,
      total: <b>{totalProvidersPerLOB[ClaimsActivityLineOfBusinessTypeEnum.Total].toLocaleString()}</b>,
      medicare: <b>{totalProvidersPerLOB[ClaimsActivityLineOfBusinessTypeEnum.Medicare].toLocaleString()}</b>,
      medicaid: <b>{totalProvidersPerLOB[ClaimsActivityLineOfBusinessTypeEnum.Medicaid].toLocaleString()}</b>,
      commercial: <b>{totalProvidersPerLOB[ClaimsActivityLineOfBusinessTypeEnum.Commercial].toLocaleString()}</b>,
      va: <b>{totalProvidersPerLOB[ClaimsActivityLineOfBusinessTypeEnum.VA].toLocaleString()}</b>,
      tricare: <b>{totalProvidersPerLOB[ClaimsActivityLineOfBusinessTypeEnum.TRICARE].toLocaleString()}</b>,
      /* oxlint-enable sort-keys */
    });

    return rows;
  }, [claimsVolumeBreakdown.data, totalProvidersPerLOB]);

  const lobColumnsToHighlight = useMemo<LobColumnKey[]>(() => {
    switch (projectContext.project.networkTaxonomy?.lineOfBusiness) {
      case "Medicare":
        return ["medicare"];
      case "Medicaid":
        return ["medicaid"];
      case "Marketplace":
      case "Employer Sponsored":
        return ["commercial"];
      case "Military":
        if (projectContext.project.networkTaxonomy?.lineOfBusinessType === "VACCN" && providerHubContext.showVaFilters) {
          return ["va"];
        } else if (projectContext.project.networkTaxonomy?.lineOfBusinessType === "TRICARE" && providerHubContext.showTriCareFilters) {
          return ["tricare"];
        }
        // else fall through to "total"
      default:
        return ["total"];
    }
  }, [projectContext.project.networkTaxonomy?.lineOfBusiness, projectContext.project.networkTaxonomy?.lineOfBusinessType, providerHubContext.showVaFilters, providerHubContext.showTriCareFilters]);

  const lobColumnHighlightRect = useColumnHighlightRect(
    providerStatusBreakdownTableWrapperRef,
    lobColumnsToHighlight,
    [claimsVolumeBreakdownTableData, providerHubContext.loading]
  );

  const effectivenessEfficiencyYAxisMax = useMemo(() => {
    const maxEffectivenessProviderCount = Math.max(...(effectivenessEfficiencyBreakdown.effectivenessData?.map(v => v.providerCount) ?? []));
    const maxEfficiencyProviderCount = Math.max(...(effectivenessEfficiencyBreakdown.efficiencyData?.map(v => v.providerCount) ?? []));
    return Math.max(maxEffectivenessProviderCount, maxEfficiencyProviderCount, 1);
  }, [effectivenessEfficiencyBreakdown.effectivenessData, effectivenessEfficiencyBreakdown.efficiencyData]);

  return (
    <PageSection>
      <>
        <ProviderHubBasicFilters />

        <div className="provider-hub-providers-table-info-header mt-4">
          <h5 className="pci-dashboard__section-header">Ready for Review</h5>
        </div>

        <div className="pci-dashboard__stackable-widget-container">
          <div className="pci-dashboard__widget pci-dashboard__ready-for-review-widget--in-network" data-testid="pci-dashboard__underperforming-in-network-providers">
            <div className="pci-dashboard__ready-for-review-widget-header">
              <div className="pci-dashboard__ready-for-review-widget-header--count">
                {readyForReview.underperformingProvidersTotalCount?.toLocaleString() ?? 0}
              </div>
              <div>
                <div className="pci-dashboard__ready-for-review-widget-header--title">Underperforming in-network providers</div>
                <div className="pci-dashboard__ready-for-review-widget-header--subtitle">Providers scoring 1-2 on both effectiveness and efficiency</div>
              </div>
              <div className="pci-dashboard__ready-for-review-widget-header--spacer" />
              {readyForReview.underperformingProvidersHasData && (
                <QESButton
                  onClick={() => providerHubContext.viewUnderperformingProviders()}
                  qesTestId="pci-dashboard__underperforming-in-network-providers--review-in-provider-hub-btn"
                >
                  Review in Provider Hub
                </QESButton>
              )}
            </div>
            {readyForReview.underperformingProvidersHasData ? (
              <ReadyForReviewSpecialtyList
                data={readyForReview.underperformingProvidersData}
                onSpecialtyClick={(s) => providerHubContext.viewUnderperformingProviders(s)}
                qesTestId="pci-dashboard__underperforming-in-network-providers"
              />
            ) : (
              <EmptyWidgetMessage loading={readyForReview.isLoading} error={readyForReview.isError} qesTestId="pci-dashboard__underperforming-in-network-providers" />
            )}
          </div>

          <div className="pci-dashboard__widget pci-dashboard__ready-for-review-widget--out-of-network" data-testid="pci-dashboard__network-expansion-opportunities">
            <div className="pci-dashboard__ready-for-review-widget-header">
              <div className="pci-dashboard__ready-for-review-widget-header--count">
                {readyForReview.networkExpansionOpportunitiesTotalCount?.toLocaleString() ?? 0}
              </div>
              <div>
                <div className="pci-dashboard__ready-for-review-widget-header--title">Network Expansion Opportunities</div>
                <div className="pci-dashboard__ready-for-review-widget-header--subtitle">Out-of-network providers scoring 4-5 on both effectiveness and efficiency</div>
              </div>
              <div className="pci-dashboard__ready-for-review-widget-header--spacer" />
              {readyForReview.networkExpansionOpportunitiesHasData && (
                <QESButton
                  onClick={() => providerHubContext.viewNetworkExpansionOpportunityProviders()}
                  qesTestId="pci-dashboard__network-expansion-opportunities--review-in-provider-hub-btn"
                >
                  Review in Provider Hub
                </QESButton>
              )}
            </div>
            {readyForReview.networkExpansionOpportunitiesHasData ? (
              <ReadyForReviewSpecialtyList
                data={readyForReview.networkExpansionOpportunitiesData}
                onSpecialtyClick={(s) => providerHubContext.viewNetworkExpansionOpportunityProviders(s)}
                qesTestId="pci-dashboard__network-expansion-opportunities"
              />
            ) : (
              <EmptyWidgetMessage loading={readyForReview.isLoading} error={readyForReview.isError} qesTestId="pci-dashboard__network-expansion-opportunities" />
            )}
          </div>
        </div>

        <div className="provider-hub-providers-table-info-header mt-4">
          <div className="pci-dashboard__table-info-header-subtitle">
            <h5 className="pci-dashboard__section-header">Provider Status Breakdown</h5>
            <span className="pci-dashboard__description-text">Filtered providers · claims activity classification · click any count to open in Provider Hub</span>
          </div>
        </div>

        <div
          className="pci-dashboard__claims-volume-breakdown"
          ref={providerStatusBreakdownTableWrapperRef}
          hidden={!claimsVolumeBreakdown.hasData}
          data-testid="pci-dashboard__claims-volume-breakdown--waffles-table"
        >
          <Waffles<ClaimsVolumeBreakdownWaffleToppings>
            columns={providerStatusBreakdownColumnConfig}
            data={claimsVolumeBreakdownTableData}
            fullWidth={true}
            rowDecorator={(row) => row.key === "totals-footer" ? "pci-dashboard__claims-volume-breakdown-totals-row" : ""}
            testId="pci-dashboard__claims-volume-breakdown-table"
          />
          {lobColumnHighlightRect && (
            <div
              aria-hidden="true"
              className="pci-dashboard__claims-volume-breakdown-column-highlight"
              data-testid="pci-dashboard__claims-volume-breakdown--current-lob-column-highlight"
              style={lobColumnHighlightRect}
            />
          )}
        </div>

        {!claimsVolumeBreakdown.hasData && (
          <div className="pci-dashboard__widget pci-dashboard__claims-volume-breakdown-empty-widget">
            <table className="waffles-table w-100" aria-hidden="true">
              <thead>
                <tr className="waffles-head-row">
                  {providerStatusBreakdownVisibleColumns.map(([columnKey, column]) => (
                    <th
                      key={columnKey}
                      className="waffles-head no-sort"
                      style={{ minWidth: column.minWidth ?? "auto", textAlign: column.alignment ?? TextAlignment.Left }}
                    >
                      <div className="horizontal-header-title waffles-head-content">
                        <span className="waffles-head-title w-100">{column.displayText}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
            <EmptyWidgetMessage loading={claimsVolumeBreakdown.isLoading} error={claimsVolumeBreakdown.isError} qesTestId="pci-dashboard__claims-volume-breakdown" />
          </div>
        )}

        <div className="provider-hub-providers-table-info-header mt-4">
          <div className="pci-dashboard__table-info-header-subtitle">
            <h5 className="pci-dashboard__section-header">Effectiveness & Efficiency Scores</h5>
            <span className="pci-dashboard__description-text">Filtered providers · click any bar to open in Provider Hub</span>
          </div>
        </div>

        <div className="pci-dashboard__stackable-widget-container">
          <div className="pci-dashboard__widget pci-dashboard__effectiveness-efficiency-widget" data-testid="pci-dashboard__effectiveness-score-chart">
            <div className="pci-dashboard__widget-header">Effectiveness</div>
            {effectivenessEfficiencyBreakdown.effectivenessHasData ? (
              <ProviderScoreBarChart
                data={effectivenessEfficiencyBreakdown.effectivenessData}
                onBarClick={(score) => providerHubContext.viewProvidersFilteredByScore("effectiveness", score)}
                xAxisTitle="Effectiveness Score"
                yAxisTitle="Provider Count"
                yAxisMax={effectivenessEfficiencyYAxisMax}
              />
            ) : (
              <EmptyWidgetMessage loading={effectivenessEfficiencyBreakdown.isLoading} error={effectivenessEfficiencyBreakdown.isError} qesTestId="pci-dashboard__effectiveness-score-chart" />
            )}
          </div>

          <div className="pci-dashboard__widget pci-dashboard__effectiveness-efficiency-widget" data-testid="pci-dashboard__efficiency-score-chart">
            <div className="pci-dashboard__widget-header">Efficiency</div>
            {effectivenessEfficiencyBreakdown.efficiencyHasData ? (
              <ProviderScoreBarChart
                data={effectivenessEfficiencyBreakdown.efficiencyData}
                onBarClick={(score) => providerHubContext.viewProvidersFilteredByScore("efficiency", score)}
                xAxisTitle="Efficiency Score"
                yAxisTitle="Provider Count"
                yAxisMax={effectivenessEfficiencyYAxisMax}
              />
            ) : (
              <EmptyWidgetMessage loading={effectivenessEfficiencyBreakdown.isLoading} error={effectivenessEfficiencyBreakdown.isError} qesTestId="pci-dashboard__efficiency-score-chart" />
            )}
          </div>
        </div>
      </>
    </PageSection>
  );
};

export default ProviderHubProviderClaimsInsightsView;
